import { useState } from "react";
import { usePathname } from "next/navigation";
import { FieldProps } from "@rjsf/utils";
import { CollapsibleFieldTemplateProps } from "@/src/types/components/ScenarioForm";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { RadarChart } from "@/src/components/RadarChart";
import { AnswersNode } from "@/src/types/utils/findAnswersByTitles";
import TitleFieldTemplate from "../TitleFieldTemplate";
import { usePDFContext } from "@/src/contexts/PDFContext";

const CollapsibleFieldTemplate = ({
  id,
  help,
  title,
  required,
  schema,
  description,
  errors,
  properties,
  formData = {},
}: CollapsibleFieldTemplateProps) => {

  const [collapsed, setCollapsed] = useState<boolean>(false);
  const pathname = usePathname();
  const { isPDF, currentPage } = usePDFContext();

  // In PDF mode, always keep sections expanded for proper content visibility
  const isActuallyCollapsed = isPDF ? false : collapsed;

  // const YAxisLabels = formData.properties.map(prop => prop.title)
  // console.log(YAxisLabels)
  const yaxislabels = Object.values(schema.properties).map(value => (value as
    {
      title: string;
    }
  ).title)

  // Check if the current page is the duurzame-context page
  // In normal web context, use pathname; in PDF context, use currentPage from PDFContext
  let showRadarChart = false;
  
  if (isPDF) {
    // In PDF context, check the currentPage from PDFContext
    showRadarChart = currentPage?.toLowerCase().includes("duurzame-context") ?? false;
  } else {
    // In normal web context, use pathname if available
    showRadarChart = pathname?.includes("duurzame-context") ?? false;
  }

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className={`w-full bg-white rounded-xl shadow-md pb-2`} id={id}>
      <div className="form-group mb-3">
        <div
          className="flex justify-between items-center p-6 rounded cursor-pointer pb-4"
          onClick={isPDF ? undefined : toggleCollapse} // Disable click in PDF mode
        >
          <TitleFieldTemplate
            id={id}
            title={title}
            required={required}
            description={description}
            depth={0}
          />

          {!isPDF && ( // Hide toggle button in PDF mode
            <button type="button" className="text-blue-500">
              {collapsed ? (
                <ChevronDownIcon className="w-6 h-6 text-primary_800" />
              ) : (
                <ChevronUpIcon className="w-6 h-6 text-primary_800" />
              )}
            </button>
          )}
        </div>
        {!isActuallyCollapsed && (
          <div className="pl-6 pr-16">
            {description && (
              <div className="mb-4">
                {typeof description === 'string' ? (
                  <div dangerouslySetInnerHTML={{ __html: description }} />
                ) : (
                  description
                )}
              </div>
            )}
            <div className={`flex flex-col ${showRadarChart ? 'md:flex-row' : ''} gap-4`}>
              <div className={showRadarChart ? "w-full md:w-1/3" : "w-full flex flex-col gap-4"}>
                {properties.map((prop: FieldProps, index: number) => (
                  <div key={index}>
                    {prop.content}
                  </div>
                ))}
              </div>
              {showRadarChart && (
                <div className="w-full md:w-2/3 mt-6">
                  <RadarChart
                    formJson={schema}
                    answersJson={formData as AnswersNode}
                    questionTitles={yaxislabels}
                    color="#22c55e"
                  />
                </div>
              )}
            </div>
            {errors && <div className="text-red-500">{errors}</div>}
            {help && <div className="text-gray-500">{help}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollapsibleFieldTemplate;
